import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageSquare, Users, UserCheck, X, Check, Mail, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { interestAPI, messagesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import BackButton from '../components/ui/BackButton';

const ConnectionsPage = () => {
  const [activeTab, setActiveTab] = useState('interests'); // 'interests' or 'messages'
  const [interests, setInterests] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    received: 0,
    sent: 0,
    mutual: 0,
    unreadMessages: 0
  });
  
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [interestsData, conversationsData, sentInterestsData] = await Promise.all([
        interestAPI.getReceived(),
        messagesAPI.getConversations(),
        interestAPI.getSent()
      ]);
      
      const receivedInterests = interestsData.interests || [];
      const sentInterests = sentInterestsData.interests || [];
      const mutualCount = receivedInterests.filter(i => i.status === 'mutual').length;
      
      // conversationsData is the array directly from backend
      const conversationsList = Array.isArray(conversationsData) ? conversationsData : [];
      
      setInterests(receivedInterests);
      setConversations(conversationsList);
      setStats({
        received: receivedInterests.length,
        sent: sentInterests.length,
        mutual: mutualCount,
        unreadMessages: conversationsList.filter(c => c.unreadCount > 0).length
      });
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load connections data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInterest = async (interestId) => {
    try {
      await interestAPI.respond(interestId, 'accepted');
      toast({
        title: "Interest Accepted!",
        description: "You can now start messaging each other.",
      });
      loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to accept interest",
        variant: "destructive",
      });
    }
  };

  const handleRejectInterest = async (interestId) => {
    try {
      await interestAPI.respond(interestId, 'rejected');
      toast({
        title: "Interest Declined",
        description: "This profile has been removed from your interests.",
      });
      loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to decline interest",
        variant: "destructive",
      });
    }
  };

  const handleViewProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const handleOpenChat = (conversationId, otherUserId) => {
    navigate(`/messages/${otherUserId}`, { 
      state: { returnUrl: '/connections', from: 'connections' } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full px-2">
        <BackButton />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Connections</h1>
          <p className="text-gray-600">Manage your interests and messages in one place</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="bg-gradient-to-br from-pink-500 to-navy-600 text-white cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
            onClick={() => setActiveTab('interests')}
          >
            <CardContent className="p-4">
              <Heart className="w-8 h-8 mb-2" />
              <p className="text-2xl font-bold">{stats.received}</p>
              <p className="text-sm opacity-90">Received</p>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-purple-500 to-pink-600 text-white cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
            onClick={() => setActiveTab('interests')}
          >
            <CardContent className="p-4">
              <Users className="w-8 h-8 mb-2" />
              <p className="text-2xl font-bold">{stats.sent}</p>
              <p className="text-sm opacity-90">Sent</p>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-green-500 to-teal-600 text-white cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
            onClick={() => setActiveTab('interests')}
          >
            <CardContent className="p-4">
              <UserCheck className="w-8 h-8 mb-2" />
              <p className="text-2xl font-bold">{stats.mutual}</p>
              <p className="text-sm opacity-90">Mutual</p>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white cursor-pointer hover:shadow-lg transition-all transform hover:scale-105"
            onClick={() => setActiveTab('messages')}
          >
            <CardContent className="p-4">
              <MessageSquare className="w-8 h-8 mb-2" />
              <p className="text-2xl font-bold">{stats.unreadMessages}</p>
              <p className="text-sm opacity-90">Unread</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('interests')}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
              activeTab === 'interests'
                ? 'bg-mint-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Heart className="w-5 h-5 inline-block mr-2" />
            Interests ({stats.received})
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
              activeTab === 'messages'
                ? 'bg-mint-500 text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <MessageSquare className="w-5 h-5 inline-block mr-2" />
            Messages ({conversations.length})
          </button>
        </div>

        {/* Content */}
        {activeTab === 'interests' ? (
          /* Interests Tab */
          <div>
            {interests.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Heart className="w-16 h-16 mx-auto text-coral-500 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Interests Yet</h3>
                  <p className="text-gray-500 mb-4">When someone shows interest in your profile, they will appear here.</p>
                  <Button onClick={() => navigate('/search')} className="bg-mint-500 hover:bg-mint-600">
                    Browse Profiles
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {interests.map((interest) => (
                  <Card key={interest.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-pink-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                            {interest.fromUser?.firstName?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                              {interest.fromUser?.firstName} {interest.fromUser?.lastName}
                              {(interest.fromUser?.isVerified || interest.fromUser?.govIdVerified) && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                  <CheckCircle className="w-3 h-3" />
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {interest.fromUser?.age} years • {interest.fromUser?.location}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center mt-1">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(interest.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {interest.status === 'mutual' && (
                          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                            Mutual
                          </span>
                        )}
                      </div>

                      {interest.message && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-700 italic">"{interest.message}"</p>
                        </div>
                      )}

                      <div className="flex space-x-3">
                        <Button
                          onClick={() => handleViewProfile(interest.fromUserId)}
                          variant="outline"
                          className="flex-1"
                        >
                          View Profile
                        </Button>
                        {interest.status === 'pending' && (
                          <>
                            <Button
                              onClick={() => handleAcceptInterest(interest.id)}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Accept
                            </Button>
                            <Button
                              onClick={() => handleRejectInterest(interest.id)}
                              variant="outline"
                              className="flex-1 text-navy-500 border-mint-500 hover:bg-cream-100"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Decline
                            </Button>
                          </>
                        )}
                        {interest.status === 'mutual' && (
                          <Button
                            onClick={() => handleOpenChat(null, interest.fromUserId)}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Messages Tab */
          <div>
            {conversations.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Messages Yet</h3>
                  <p className="text-gray-500 mb-4">Start a conversation with someone you're interested in!</p>
                  <Button onClick={() => navigate('/search')} className="bg-mint-500 hover:bg-mint-600">
                    Find Matches
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {conversations.map((conversation) => (
                  <Card 
                    key={conversation.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => handleOpenChat(conversation.id, conversation.otherUser.id)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full flex items-center justify-center text-white text-lg font-bold">
                            {conversation.otherUser?.firstName?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-lg flex items-center gap-2">
                                {conversation.otherUser?.firstName} {conversation.otherUser?.lastName}
                                {(conversation.otherUser?.isVerified || conversation.otherUser?.govIdVerified) && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                    <CheckCircle className="w-3 h-3" />
                                  </span>
                                )}
                              </h3>
                              <span className="text-xs text-gray-500">
                                {new Date(conversation.lastMessageAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className={`text-sm ${conversation.unreadCount > 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                              {conversation.lastMessage}
                            </p>
                          </div>
                        </div>
                        {conversation.unreadCount > 0 && (
                          <div className="ml-4 bg-mint-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                            {conversation.unreadCount}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionsPage;
